import React, { CSSProperties, PropsWithChildren, useEffect } from "react";
import ReactDOM from "react-dom";
import { createSlot } from "react-slotify";
import cn from "classnames";
import { Button } from "../Button";
import { useTranslation } from "../../hooks/useTranslation";

export enum CloseModalReason {
  SUCCESS = "success",
  CANCEL = "cancel",
  ERROR = "error",
}

export type CloseModalPayload<T = undefined> = {
  closeReason: CloseModalReason;
  payload?: T;
};

export interface BaseModalProps {
  visible: boolean;
  fullscreen?: boolean;

  title?: string;

  classNameSuccessButton?: string;
  classNameCancelButton?: string;
  containerClassName?: string;
  containerStyle?: CSSProperties;

  successButtonTitle?: string;
  cancelButtonTitle?: string;

  showSuccessButton?: boolean;
  showCancelButton?: boolean;

  disableSuccessButton?: boolean;
  disableCancelButton?: boolean;

  loading?: boolean;

  onSuccessClick?: () => Promise<any> | void;
  onCancelClick?: () => Promise<any> | void;
}

const modalPortal = document.getElementById("modal-portal");
export const ModalHeaderSlot = createSlot();
export const ModalBodySlot = createSlot();
export const ModalFooterButtonsSlot = createSlot();

export const BaseModal: React.FC<PropsWithChildren<BaseModalProps>> = ({
  loading,
  disableCancelButton,
  disableSuccessButton,
  containerClassName,
  containerStyle,
  fullscreen,
  onCancelClick,
  onSuccessClick,
  title,
  classNameCancelButton,
  classNameSuccessButton,
  successButtonTitle,
  cancelButtonTitle,
  showCancelButton = true,
  showSuccessButton = true,
  visible,
  children,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    visible && (document.body.style.overflow = "hidden");
    !visible && (document.body.style.overflow = "unset");
  }, [visible]);

  if (visible == null || !visible) {
    return null;
  }

  return (
    <>
      {ReactDOM.createPortal(
        <div
          className="relative z-10"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
              <div
                className={cn(
                  "relative dark:text-white bg-gray-50 dark:bg-neutral-600 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full",
                  containerClassName,
                )}
                style={containerStyle}
              >
                <div className="px-3 py-3">
                  <div className="sm:flex sm:items-start w-full">
                    <div className="w-full">
                      <h3
                        id="modal-title"
                        className="text-lg leading-6 pb-3 font-medium text-gray-500 dark:text-white"
                      >
                        {title}
                      </h3>
                      <hr className={"my-1"} />
                      <div className="mt-3">
                        <p
                          style={{ whiteSpace: "pre-line" }}
                          className="text-sm text-gray-500 dark:text-white"
                        >
                          {children}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-2 py-3 gap-2 dark:bg-neutral-600 flex justify-end">
                  {showCancelButton && (
                    <Button
                      label={cancelButtonTitle ?? t("cancel").toString()}
                      disabled={disableCancelButton}
                      onClick={onCancelClick}
                      className={cn(classNameCancelButton)}
                    />
                  )}
                  {showSuccessButton && (
                    <Button
                      loading={loading}
                      label={successButtonTitle ?? t("confirm").toString()}
                      disabled={disableSuccessButton || loading}
                      className={cn(classNameSuccessButton)}
                      onClick={onSuccessClick}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        modalPortal as Element,
      )}
    </>
  );
};
